import { lazy, Suspense, useRef, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import AppShell from "./components/layout/AppShell";
import AdminGuard from "./components/admin/AdminGuard";
import SplashScreen from "./components/loaders/SplashScreen";
import SCSpinner from "./components/loaders/SCSpinner";
import Home from "./pages/Home";
import Categories from "./pages/Categories";
import CategoryProducts from "./pages/CategoryProducts";
import ProductDetail from "./pages/ProductDetail";
import Search from "./pages/Search";
import Wishlist from "./pages/Wishlist";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Account from "./pages/Account";
import Privacy from "./pages/legal/Privacy";
import Terms from "./pages/legal/Terms";
import NotFound from "./pages/NotFound";

// Admin is a separate bundle chunk, loaded only when someone actually
// navigates to /admin/* — the storefront (what most visitors use) never
// pays for it. See the Suspense fallback below for the loading state.
const AdminLayout = lazy(() => import("./components/layout/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/admin/Login"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminProducts = lazy(() => import("./pages/admin/Products"));
const AdminProductForm = lazy(() => import("./pages/admin/ProductForm"));
const AdminCategories = lazy(() => import("./pages/admin/Categories"));
const AdminCategoryForm = lazy(() => import("./pages/admin/CategoryForm"));
const AdminOrders = lazy(() => import("./pages/admin/Orders"));
const AdminCoupons = lazy(() => import("./pages/admin/Coupons"));
const AdminReviews = lazy(() => import("./pages/admin/Reviews"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

function AdminFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas">
      <SCSpinner size={56} />
    </div>
  );
}

function App() {
  const appRef = useRef(null);
  const [splashDone, setSplashDone] = useState(false);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Starts invisible; the splash's own timeline crossfades this in
          (see useSplashAnimation) so there's no hard cut on handoff. */}
      <div ref={appRef} style={{ opacity: 0 }}>
        <BrowserRouter>
          <Suspense fallback={<AdminFallback />}>
            <Routes>
              <Route element={<AppShell />}>
                <Route index element={<Home />} />
                <Route path="categories" element={<Categories />} />
                <Route path="category/:slug" element={<CategoryProducts />} />
                <Route path="product/:slug" element={<ProductDetail />} />
                <Route path="search" element={<Search />} />
                <Route path="wishlist" element={<Wishlist />} />
                <Route path="cart" element={<Cart />} />
                <Route path="checkout" element={<Checkout />} />
                <Route path="account" element={<Account />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="terms" element={<Terms />} />
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="admin/login" element={<AdminLogin />} />
              <Route
                path="admin"
                element={
                  <AdminGuard>
                    <AdminLayout />
                  </AdminGuard>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/new" element={<AdminProductForm />} />
                <Route path="products/:uuid/edit" element={<AdminProductForm />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="categories/new" element={<AdminCategoryForm />} />
                <Route path="categories/:uuid/edit" element={<AdminCategoryForm />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
      <Toaster position="top-center" richColors />
      {!splashDone && <SplashScreen appRef={appRef} onFinish={() => setSplashDone(true)} />}
    </QueryClientProvider>
  );
}

export default App;
